import { FilterProject } from '@shared/api/filters-ws';
import { concatArray } from '@shared/lib';
import { Client, ClientsFilter } from '../model/types';

export type OrderDirection = 'ASC' | 'DESC';

export interface ParsedOrderToken {
  field: string;
  direction: OrderDirection;
}

const ORDER_DIRECTION_PATTERN = /^(.*)_(ASC|DESC)$/;

export const parseOrderToken = (orderToken: string): ParsedOrderToken | null => {
  const match = ORDER_DIRECTION_PATTERN.exec(orderToken);

  if (!match) return null;

  return {
    field: match[1],
    direction: match[2] as OrderDirection,
  };
};

export const resolveProjectLabel = (token: string, catalog: FilterProject[] = []) => {
  const found = catalog.find((project) => project.code === token || project.name === token);

  if (!found) {
    return { name: token, id: token };
  }

  return {
    name: found.name || found.code,
    id: found.code || found.name,
  };
};

const getClientFio = (client: Client) => concatArray([client.lastName, client.firstName, client.surname]).toLowerCase();

const getManagerFio = (client: Client) =>
  concatArray([client.manager?.lastName, client.manager?.firstName, client.manager?.surname]).toLowerCase();

const getClientProjectTokens = (client: Client) =>
  (client.projects || []).flatMap((item) =>
    [item.project?.name, item.project?.id].filter((token): token is string => Boolean(token))
  );

export const matchesSearch = (client: Client, searchString: string) => {
  if (!searchString.trim()) return true;
  return getClientFio(client).includes(searchString.trim().toLowerCase());
};

export const matchesFilter = (client: Client, filter: ClientsFilter) => {
  if (filter.projectName?.length) {
    const projectTokens = getClientProjectTokens(client);
    if (!filter.projectName.some((value) => projectTokens.includes(value))) {
      return false;
    }
  }

  if (filter.invitationStatus?.length) {
    const invitation = (client.ticket.sminexClubInvitation || '').toUpperCase();
    if (!filter.invitationStatus.map((status) => status.toUpperCase()).includes(invitation)) {
      return false;
    }
  }

  if (filter.reconfirmationStatus?.length) {
    const reconfirmationStatus = (client.ticket.sminexClubReconfirmationStatus || '').toUpperCase();
    if (!filter.reconfirmationStatus.map((status) => status.toUpperCase()).includes(reconfirmationStatus)) {
      return false;
    }
  }

  if (filter.checked !== null && filter.checked !== undefined) {
    if (client.ticket.checked !== filter.checked) {
      return false;
    }
  }

  return true;
};

const getSortValue = (client: Client, field: string): string | number => {
  switch (field) {
    case 'NAME':
      return getClientFio(client);
    case 'MANAGER':
      return getManagerFio(client);
    case 'BROKER':
      return (client.brokerName || '').toLowerCase();
    case 'ADDITIONAL_GUEST':
      return client.ticket.sminexClubAdditional
        .map(({ fio }) => fio)
        .join(', ')
        .toLowerCase();
    case 'DEALS_SUMM':
      return (client.projects || [])
        .map((item) => item.project?.name || '')
        .join(', ')
        .toLowerCase();
    case 'INVITATION':
      return (client.ticket.sminexClubInvitation || '').toUpperCase();
    case 'RECONFIRMATION_STATUS':
      return (client.ticket.sminexClubReconfirmationStatus || '').toUpperCase();
    case 'PRESENCE':
      return client.ticket.checked ? 1 : 0;
    default:
      return '';
  }
};

export const compareByOrder = (first: Client, second: Client, orderToken: string) => {
  const parsed = parseOrderToken(orderToken);

  if (!parsed) return 0;

  const multiplier = parsed.direction === 'DESC' ? -1 : 1;
  const firstValue = getSortValue(first, parsed.field);
  const secondValue = getSortValue(second, parsed.field);

  if (firstValue < secondValue) return -1 * multiplier;
  if (firstValue > secondValue) return 1 * multiplier;
  return 0;
};

export const filterAndSortSminexClubClients = (
  clients: Client[],
  searchString: string,
  filter: ClientsFilter,
  orderBy: string[] | null
) => {
  const next = clients.filter((client) => matchesSearch(client, searchString) && matchesFilter(client, filter));

  if (!orderBy?.length) {
    return next;
  }

  return [...next].sort((first, second) => {
    for (const token of orderBy) {
      const comparison = compareByOrder(first, second, token);
      if (comparison !== 0) return comparison;
    }
    return 0;
  });
};
